import {
  fetchPortalPublications,
  findPortalPublicationRoute,
  PORTAL_PUBLICATION_STATUSES,
  publishPortalRoutes,
} from "./portalPublicationsApi.js";
import { fetchWithSupabaseAuth } from "./supabaseClient.js";

export const TODAY_STALE_PORTAL_ROUTES_URL = "/api/driver-portal/today-stale-routes";
export const TODAY_STALE_PORTAL_AUTOMATIC_WITHDRAWAL_REASONS = Object.freeze([
  "plan_changed",
  "source_data_changed",
  "source_import_changed",
  "driver_assignment_changed",
  "tractor_assignment_changed",
  "driver_account_deleted",
  "driver_identity_unverified",
]);

export const TODAY_STALE_PORTAL_WITHDRAWAL_REASON_LABELS = Object.freeze({
  plan_changed: "Tournée modifiée",
  source_data_changed: "Mise à jour de commandes",
  source_import_changed: "Import de commandes",
  driver_assignment_changed: "Chauffeur changé",
  tractor_assignment_changed: "Camion changé",
  driver_account_deleted: "Compte supprimé",
  driver_identity_unverified: "Identité à confirmer",
});

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDispatchPeriod(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

function isAutomaticWithdrawalReason(value) {
  return TODAY_STALE_PORTAL_AUTOMATIC_WITHDRAWAL_REASONS.includes(normalizeInlineText(value));
}

export function resolveTodayStalePortalWithdrawalReasonLabel(value) {
  const normalizedReason = normalizeInlineText(value);
  return TODAY_STALE_PORTAL_WITHDRAWAL_REASON_LABELS[normalizedReason]
    || (normalizedReason ? "La tournée publiée a changé" : "");
}

export function buildTodayStalePortalRouteKey(route = null) {
  const normalizedRoute = normalizeTodayStalePortalRoute(route);
  if (!normalizedRoute.truckId) {
    return "";
  }
  return `${normalizedRoute.dispatchPeriod}:${normalizedRoute.truckId}`;
}

export function buildTodayStalePortalDispatchUrl(dispatchDate = "", dispatchPeriod = "day") {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  if (!safeDispatchDate) {
    return "/results";
  }
  const searchParams = new URLSearchParams({ dispatch_date: safeDispatchDate });
  if (normalizeDispatchPeriod(dispatchPeriod) === "evening") {
    searchParams.set("dispatch_period", "evening");
  }
  return `/results?${searchParams.toString()}`;
}

export function normalizeTodayStalePortalRoute(route = null, defaults = {}) {
  const source = route && typeof route === "object" ? route : {};
  const truckId = normalizeInlineText(source.truck_id || source.truckId);
  const dispatchPeriod = normalizeDispatchPeriod(
    source.dispatch_period || source.dispatchPeriod || defaults.dispatchPeriod,
  );
  const withdrawalReason = normalizeInlineText(source.withdrawal_reason || source.withdrawalReason);
  return {
    dispatchDate: normalizeInlineText(source.dispatch_date || source.dispatchDate || defaults.dispatchDate),
    dispatchPeriod,
    driverName: normalizeInlineText(source.driver_name || source.driverName),
    truckId,
    truckName: normalizeInlineText(source.truck_name || source.truckName) || truckId,
    withdrawalReason,
    withdrawalReasonLabel: resolveTodayStalePortalWithdrawalReasonLabel(withdrawalReason),
    withdrawnAt: normalizeInlineText(source.withdrawn_at || source.withdrawnAt),
  };
}

export function formatTodayStalePortalRouteLabel(route = null) {
  const normalizedRoute = normalizeTodayStalePortalRoute(route);
  const truckLabel = normalizedRoute.truckName || normalizedRoute.truckId;
  const driverLabel = normalizedRoute.driverName;
  const periodLabel = normalizedRoute.dispatchPeriod === "evening" ? "soir" : "";
  const identity = driverLabel && truckLabel
    ? `${driverLabel} · ${truckLabel}`
    : driverLabel || truckLabel || "Tournée";
  return periodLabel ? `${identity} (${periodLabel})` : identity;
}

export function normalizeTodayStalePortalRoutes(payload = null) {
  const source = payload && typeof payload === "object" ? payload : {};
  const dispatchDate = normalizeInlineText(source.dispatch_date || source.dispatchDate);
  const routes = (Array.isArray(source.routes) ? source.routes : [])
    .map((route) => normalizeTodayStalePortalRoute(route, { dispatchDate }))
    .filter((route) => route.truckId && isAutomaticWithdrawalReason(route.withdrawalReason));

  return {
    dispatchDate,
    routes,
  };
}

function resolvePortalPublicationErrorMessage(payload, fallbackMessage) {
  const candidates = [payload?.detail, payload?.error, payload?.message];
  const matched = candidates.find((value) => typeof value === "string" && value.trim());
  return matched?.trim() || fallbackMessage;
}

export async function fetchTodayStalePortalRoutes() {
  const response = await fetchWithSupabaseAuth(TODAY_STALE_PORTAL_ROUTES_URL, { cache: "no-store" });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(resolvePortalPublicationErrorMessage(
      payload,
      "Impossible de charger les tournées portail à republier.",
    ));
  }
  return normalizeTodayStalePortalRoutes(payload);
}

export function resolveTodayStalePortalPublishRoute(portalPublications, truckId) {
  const route = findPortalPublicationRoute(portalPublications, truckId);
  if (!route) {
    throw new Error("Cette tournée n'est plus disponible pour publication.");
  }
  if (route.status === PORTAL_PUBLICATION_STATUSES.published) {
    return { alreadyPublished: true, route };
  }
  if (route.publicationAllowed === false) {
    throw new Error(
      route.publicationUnavailableReason
        || "Impossible de publier cette tournée dans le portail chauffeur.",
    );
  }
  if (!route.planHash || route.status !== PORTAL_PUBLICATION_STATUSES.unpublished) {
    throw new Error("Le plan actuel de cette tournée est introuvable. Ouvrez le dispatch pour republier.");
  }
  return { alreadyPublished: false, route };
}

export async function publishTodayStalePortalRoute(route = null) {
  const normalizedRoute = normalizeTodayStalePortalRoute(route);
  if (!normalizedRoute.truckId || !normalizedRoute.dispatchDate) {
    throw new Error("Impossible de publier cette tournée dans le portail chauffeur.");
  }

  const publications = await fetchPortalPublications(
    normalizedRoute.dispatchDate,
    normalizedRoute.dispatchPeriod,
  );
  const resolved = resolveTodayStalePortalPublishRoute(publications, normalizedRoute.truckId);
  if (resolved.alreadyPublished) {
    return { alreadyPublished: true, payload: null };
  }

  const payload = await publishPortalRoutes(
    normalizedRoute.dispatchDate,
    [resolved.route],
    normalizedRoute.dispatchPeriod,
  );
  return { alreadyPublished: false, payload };
}
